import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, ArrowLeft } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { fmtBRL } from "@/lib/format";
import { installmentsOf } from "@/lib/pricing";
import { imgVariant } from "@/lib/img";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { ShippingCalculator } from "@/components/store/ShippingCalculator";

export default function CartPage() {
  const { items, setQty, removeItem, subtotal } = useCart();
  const settings = useSettings();
  const cardSubtotal = items.reduce((s, i) => s + (i.price_card || i.price) * i.qty, 0);
  const cardMaxInst = items.reduce((m, i) => Math.min(m, i.max_installments || 12), 12);

  if (items.length === 0) {
    return (
      <div className="container-store py-20 text-center" data-testid="cart-empty">
        <ShoppingBag className="w-16 h-16 text-gray-200 mx-auto" />
        <h1 className="mt-6 font-heading text-2xl font-bold text-gray-900">Seu carrinho está vazio</h1>
        <p className="mt-2 text-gray-500">Explore nosso catálogo e encontre o móvel ideal para sua casa.</p>
        <Link to="/produtos">
          <Button className="mt-8 rounded-full px-8" data-testid="cart-empty-cta">
            Ver Produtos <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="container-store py-8 sm:py-12" data-testid="cart-page">
      <Seo title="Meu Carrinho | HG Móveis" noindex />
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
        Meu Carrinho
      </h1>
      <p className="text-sm text-gray-500 mt-1">{items.length} item(ns) no carrinho</p>

      <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-8 items-start">
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={`${item.id}-${item.color}`}
              className="flex gap-4 bg-white border border-gray-100 rounded-xl p-3 sm:p-4"
              data-testid={`cart-item-${item.id}`}
            >
              <Link to={`/produto/${item.id}`} className="shrink-0">
                <img
                  src={imgVariant(item.image, "thumb") || "/assets/logo.png"}
                  alt={item.name}
                  loading="lazy"
                  decoding="async"
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-contain bg-white border border-gray-100"
                />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/produto/${item.id}`}>
                  <h3 className="font-medium text-sm sm:text-base text-gray-800 line-clamp-2 hover:text-brand">
                    {item.name}
                  </h3>
                </Link>
                {item.color && <p className="text-xs text-gray-500 mt-0.5">Cor: {item.color}</p>}
                <p className="text-sm mt-0.5">
                  <span className="text-[#D71920] font-bold">{fmtBRL(item.price)}</span>
                </p>
                <p className="text-[11px] text-gray-400">
                  Ou até {item.max_installments || 12}x de {fmtBRL(installmentsOf(item.price_card || item.price, item.max_installments || 12)[0])} no cartão de crédito
                </p>
                <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center border border-gray-200 rounded-full">
                    <button
                      className="p-1.5 text-gray-600 hover:text-brand"
                      onClick={() => setQty(item.id, item.color, item.qty - 1)}
                      data-testid={`cart-qty-minus-${item.id}`}
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-8 text-center text-sm font-semibold" data-testid={`cart-qty-${item.id}`}>
                      {item.qty}
                    </span>
                    <button
                      className="p-1.5 text-gray-600 hover:text-brand"
                      onClick={() => setQty(item.id, item.color, item.qty + 1)}
                      data-testid={`cart-qty-plus-${item.id}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <span className="font-heading font-bold text-brand" data-testid={`cart-item-total-${item.id}`}>
                    {fmtBRL(item.price * item.qty)}
                  </span>
                </div>
              </div>
              <button
                className="self-start text-gray-400 hover:text-red-500 transition-colors"
                onClick={() => removeItem(item.id, item.color)}
                aria-label="Remover item"
                data-testid={`cart-remove-${item.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <aside className="bg-white border border-gray-100 rounded-2xl p-6 sticky top-40" data-testid="cart-summary">
          <h2 className="font-heading font-semibold text-lg text-gray-900">Resumo do pedido</h2>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Total</span>
              <span data-testid="cart-subtotal">{fmtBRL(subtotal)}</span>
            </div>
            <div className="flex justify-between text-gray-500">
              <span>Cartão de crédito</span>
              <span data-testid="cart-subtotal-card">
                até {cardMaxInst}x de {fmtBRL(installmentsOf(cardSubtotal, cardMaxInst)[0])}
              </span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Entrega</span>
              <span>Calculada no checkout pelo seu CEP</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between font-heading font-bold text-lg text-gray-900">
            <span>Total parcial</span>
            <span className="text-brand" data-testid="cart-total">{fmtBRL(subtotal)}</span>
          </div>
          <p className="mt-2 text-xs text-green-700 font-medium">
            {settings.payment_note}
          </p>
          <div className="mt-4">
            <ShippingCalculator />
          </div>
          <Link to="/checkout" className="block">
            <Button className="mt-5 w-full rounded-full h-12 font-semibold active:scale-95 transition-transform" data-testid="go-to-checkout">
              Finalizar Pedido <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/produtos" className="block">
            <Button variant="ghost" className="mt-2 w-full rounded-full" data-testid="continue-shopping">
              <ArrowLeft className="w-4 h-4" /> Continuar comprando
            </Button>
          </Link>
        </aside>
      </div>
    </div>
  );
}
